import { useEffect } from 'react';

import {
  selectIsExecutionPending,
  selectLatestStatus,
  useCohortDefinitionStore,
} from '@verantos-dev/app-state-cohort-definition';
import { CohortDefinitionStatus } from '@verantos-dev/types-cohorts';

import { useCohortDefinitionData } from './useCohortDefinitionData';
import type { SocketEventCallbacks } from './useCohortDefinitionSocket';
import { useCohortDefinitionSocket } from './useCohortDefinitionSocket';
import { useCohortProfileData } from './useCohortProfileData';

interface CohortDefinitionLifecycleParams {
  projectId: string;
  cohortId: string;
  cohortVersionId: string;
  enabled: boolean;
  /** Optional callbacks for Redux sync during migration. */
  socketCallbacks?: SocketEventCallbacks;
}

/**
 * Top-level orchestrator for the Zustand/TanStack cohort definition lifecycle.
 *
 * Composes:
 * - `useCohortDefinitionData` — TanStack fetch + store hydration on initial /
 *   query-driven load. Internally routes to either
 *   `useCohortDefinitionDataExplicitSave` or `useCohortDefinitionDataLegacy`
 *   based on the `explicit_save` feature flag; both call the shared
 *   `useCohortDefinitionQuery` in `feature-analytical-variables`.
 * - `useCohortProfileData` — TanStack fetch + store hydration for profile
 *   (status, version, isEditable, dataset, subject count). Uses the
 *   version-keyed `useCohortProfileQuery` in `feature-analytical-variables`
 *   so save/revert produce fresh cache entries automatically (this is
 *   different from the cohort-id-keyed canonical hook in
 *   `data-access-cohorts`; see that file's header for the split rationale).
 * - `useCohortDefinitionSocket` — socket event routing (staged changes,
 *   save, revert). Reads from Zustand and pushes back to it; takes optional
 *   Redux-bridge callbacks during the explicit_save migration but does not
 *   read from RTK Query itself.
 * - Store reset on param change and unmount cleanup.
 *
 * OR-6025 audit (Gap 16): all four compositions are on TanStack — no RTK
 * Query reads remain in the lifecycle chain. The remaining RTK reads in
 * `usePrefetchCohortData` (`useGetCohortVersionQuery`) and
 * `usePrefetchResultsData` (attrition / summary / subjects) sit outside
 * this orchestrator and are addressed by PRs 9 (results rebuild) and
 * future cohort-version migration work.
 *
 * Single entry point — `usePrefetchCohortData` calls only this.
 */
export const useCohortDefinitionLifecycle = (params: CohortDefinitionLifecycleParams) => {
  const { projectId, cohortId, cohortVersionId, enabled, socketCallbacks } = params;
  const reset = useCohortDefinitionStore((s) => s.reset);
  const setLoadedCohortId = useCohortDefinitionStore((s) => s.setLoadedCohortId);
  const loadedCohortId = useCohortDefinitionStore((s) => s.loadedCohortId);
  const storeVersionId = useCohortDefinitionStore((s) => s.profile.latestCohortVersionId);
  const latestStatus = useCohortDefinitionStore(selectLatestStatus);
  const isExecutionPending = useCohortDefinitionStore(selectIsExecutionPending);
  const shouldPoll = latestStatus === CohortDefinitionStatus.PROCESSING || isExecutionPending;

  // Reset the shared store whenever it currently holds a DIFFERENT cohort than
  // the one being opened, then tag it as belonging to this cohort. Keyed on the
  // store's own `loadedCohortId` rather than URL prev-vs-current diffing because
  // the latter no-ops on a fresh page mount (the ref initializes to the current
  // params) — leaving the previous cohort's state, including a leaked
  // `hasStagedChanges`, to survive and block re-hydration (OR-6110).
  //
  // `loadedCohortId` is read via getState() (not subscribed) and the effect runs
  // only on a cohortId/projectId change, so concurrent lifecycle instances
  // (page + prefetch + AV-edit) don't churn the store by reacting to each
  // other's `setLoadedCohortId` writes.
  //
  // Same-cohort re-mounts (CB ↔ AV edit sibling routes) keep `loadedCohortId`
  // equal to `cohortId`, so the store and any staged edits are preserved — which
  // is why the old unconditional unmount reset was removed.
  useEffect(() => {
    if (!cohortId) return;
    const loadedCohortId = useCohortDefinitionStore.getState().loadedCohortId;
    if (loadedCohortId !== undefined && loadedCohortId !== cohortId) {
      reset();
    }
    setLoadedCohortId(cohortId);
  }, [projectId, cohortId, reset, setLoadedCohortId]);

  // Use the store's version (kept current by socket events) so the TQ query
  // tracks version changes that arrive via history.replaceState (which doesn't
  // update React Router's useParams). Falls back to the route param on initial
  // load (and after the reset above clears the previous cohort's version).
  //
  // While the store still belongs to a DIFFERENT cohort (the render before the
  // reset effect runs), ignore its version and use the URL's — otherwise the
  // first render fetches the previous cohort's version and the hydration effect
  // re-writes that cohort's definition back into the just-reset store, leaving
  // it on screen until this cohort's own fetch resolves (~1s). Read-only; does
  // not feed the reset effect, so concurrent instances don't churn (OR-6110).
  const isStoreStale = !!cohortId && loadedCohortId !== cohortId;
  const effectiveVersionId = (isStoreStale ? undefined : storeVersionId) ?? cohortVersionId;

  // TQ fetch + conditional store hydration (definition: AVs, criteria, study period)
  const { isLoading, error } = useCohortDefinitionData({
    projectId,
    cohortId,
    cohortVersionId: effectiveVersionId,
    enabled,
  });

  // TQ fetch + conditional store hydration (profile: status, isEditable, versionId)
  // Poll every 5 s while processing so status updates without a socket event or manual refresh.
  useCohortProfileData({ projectId, cohortId, cohortVersionId, enabled, shouldPoll });

  // Socket event routing
  useCohortDefinitionSocket(cohortVersionId, { projectId, cohortId }, enabled, socketCallbacks);

  return { isLoading, error };
};
